/* eslint-disable functional/no-expression-statement,@typescript-eslint/ban-ts-comment */
import * as D3Scale from "d3-scale";
import * as D3Shape from "d3-shape";
import { pipe, ReadonlyArray } from "fp";

import * as Color from "~/Color";
import * as Log from "~/Log";
import { getXScaledValue } from "~/Log/Chart/StatusEvents";
import { Time } from "~/Time";

type RevisionData = {
  readonly status: string;
  readonly time: number;
};

type RevisionGrouping = {
  readonly className: string;
  readonly group: readonly RevisionData[];
};

const groupEvents = (data: readonly Log.Chart.LogChartData[]) => {
  return pipe(
    data,
    ReadonlyArray.filter<Log.Chart.LogChartData>(
      (event) => event.source === "REVISION"
    ),
    ReadonlyArray.filterWithIndex<Log.Chart.LogChartData>(
      (index) => index % 2 === 0
    ),
    ReadonlyArray.reduce<Log.Chart.LogChartData, readonly RevisionGrouping[]>(
      [],
      (revisionData, event) => {
        return [
          ...revisionData,
          {
            className: `revision-path-${event.status}-${event.start}`,
            group: [
              { status: event.status, time: event.start },
              { status: event.status, time: event.stop },
            ],
          },
        ];
      }
    )
  );
};

const isDefined = (data: RevisionData | undefined): boolean => {
  return (data && data?.time && !isNaN(data.time)) || false;
};

const lineGenerator = (
  xScale: D3Scale.ScaleTime<number, number, never>,
  yScale: Log.Chart.YScale,
  timezone: Time.Zone
) =>
  D3Shape.line<RevisionData>()
    .defined((d) => isDefined(d))
    .x((d) => getXScaledValue(d.time, xScale, timezone))
    .y((d) => (d && d?.status ? yScale(d.status) || 0 : 0));

export const generate = ({
  svg,
  revisionEvents,
  xScale,
  yScale,
  topMargin,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps): void => {
  const eventGroups = groupEvents(revisionEvents);

  const pathGroup = svg
    .append("g")
    .attr("class", "revision-path-group")
    .attr("transform", `translate(0,${topMargin - 2})`);

  eventGroups.forEach((eventGroup) =>
    pathGroup
      .append("path")
      .attr("class", eventGroup.className)
      .datum(eventGroup.group)
      .attr("fill", "none")
      .attr("stroke", Color.guardsmanRed())
      .attr("stroke-opacity", 0.3)
      .attr("stroke-width", 8)
      .attr("d", lineGenerator(xScale, yScale, defaultTimezone))
  );
};

export const update = ({
  svg,
  xScale,
  yScale,
  revisionEvents,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps) => {
  const eventGroups = groupEvents(revisionEvents);

  eventGroups.forEach((eventGroup) =>
    svg
      .selectAll<SVGPathElement, readonly Log.Chart.LogChartData[]>(
        `.${eventGroup.className}`
      )
      .datum(eventGroup.group)
      .attr("d", lineGenerator(xScale, yScale, defaultTimezone))
  );
};
